import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CopyLinkButton } from '../../components/guide/CopyLinkButton';
import { ConfigAuditPanel } from '../../components/progression/ConfigAuditPanel';
import { eventAudit } from '../../data/guide/sourceAudit';
import { eventAnchor, eventIdFromLegacyHash, eventIdFromPrettyPath } from '../../utils/prettyRoutes';
import { CalendarDays, Clock, DoorOpen, Gift, Keyboard, MapPin } from 'lucide-react';
import { EventPositionList } from '../../components/world/EventPositionList';
import { useEventPositions } from '../../hooks/useEventPositions';
import { WorldGate } from '../../components/world/WorldGate';
import { WorldPageHeader } from '../../components/world/WorldPageHeader';
import { useHashScroll } from '../../hooks/useHashScroll';
import { invasionPolicies } from '../../data/world/invasionPolicies';
import { EXPORT_DATE_LABEL } from '../../data/world/package';
import { bagContents, bagName, levelRange } from '../../utils/bagSources';
import { eventPath, itemPath, mapName, mapPath } from '../../utils/worldFormat';
import { entryPathById } from '../../utils/guideIndex';
import { eventConfigLabel, eventStatusSummary, roomStateLabel, roomStatesText, serversText } from '../../utils/eventStatus';
import type { GameEvent, WorldData } from '../../types/world';
const WEEKDAYS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const pad = (n?: number) => n === undefined ? '--' : String(n).padStart(2, '0');
type Filter = 'activos' | 'todos';
export function EventsGuide() {
  useHashScroll();
  useEffect(() => {
    document.title = 'Eventos e invasiones · Guía MU Vanguard';
  }, []);
  return <div data-id="element-2392" data-mp="15mfd5wq2yi2c">
      <WorldPageHeader crumbs={[{
      label: 'Guía',
      to: '/guia'
    }, {
      label: 'Eventos'
    }]} eyebrow="Eventos e invasiones" title="Eventos" data-id="element-2393" data-mp="15n6y8dm1wxed">
        <p className="mt-2 max-w-2xl text-sm md:text-base text-gray-400" data-id="element-2394" data-mp="91wthkkjf3r6">
          Horarios, salas y recompensas según la configuración exportada el {EXPORT_DATE_LABEL}. No es información en vivo del servidor.
        </p>
      </WorldPageHeader>
      <WorldGate data-id="element-2395" data-mp="1bg4a5102bli2">{w => <EventsList w={w} data-id="element-2396" data-mp="1bgmbud5hurxd" />}</WorldGate>
    </div>;
}
function isActive(e: GameEvent) {
  return e.activeConfigured !== false;
}
function EventsList({
  w
}: {
  w: WorldData;
}) {
  const {
    pathname,
    hash
  } = useLocation();
  const navigate = useNavigate();
  // Ruta legible /guia/eventos/<nombre>, o enlace antiguo #invasion:2 (se cambia a la legible).
  const targetId = eventIdFromPrettyPath(pathname) ?? eventIdFromLegacyHash(hash);
  const target = targetId ? w.events.get(targetId) : undefined;
  const [filter, setFilter] = useState<Filter>(target && !isActive(target) ? 'todos' : 'activos');
  useEffect(() => {
    if (!targetId) return;
    const pretty = eventPath(targetId);
    if (pretty.startsWith('/guia/eventos/') && decodeURIComponent(pathname) !== pretty) navigate(pretty, {
      replace: true
    });
    if (target) document.title = `${target.name} · Eventos · Guía MU Vanguard`;
    const t = window.setTimeout(() => document.getElementById(eventAnchor(targetId))?.scrollIntoView({
      block: 'start'
    }), 60);
    return () => window.clearTimeout(t);
  }, [targetId, target, pathname, navigate]);
  const list = useMemo(() => Array.from(w.events.values()).filter(e => filter === 'todos' || isActive(e)).sort((a, b) => Number(!isActive(a)) - Number(!isActive(b)) || a.name.localeCompare(b.name, 'es')), [w, filter]);
  if (!w.events.size) return <p className="text-gray-400" data-id="element-2397" data-mp="394geoqdnwulq">No se pudieron cargar los eventos.</p>;
  return <div data-id="element-2398" data-mp="2uftp7vubmmgs">
      <div role="group" aria-label="Filtrar eventos" className="mb-4 flex gap-1.5" data-id="element-2399" data-mp="2ug3fzln6wxnj">
        {(['activos', 'todos'] as Filter[]).map(f => <button key={f} type="button" aria-pressed={filter === f} onClick={() => setFilter(f)} className={`h-10 rounded-full px-4 text-sm font-semibold transition-colors duration-150 ${filter === f ? 'bg-[#D4AF37] text-[#050b14]' : 'border border-white/15 text-gray-300 hover:text-white'}`} data-id="element-2400" data-mp="2zdlqhgguvkxn">
            {f === 'activos' ? 'Activos' : 'Todos'}
          </button>)}
      </div>
      <ul className="grid gap-3 lg:grid-cols-2" data-id="element-2401" data-mp="1u8ggb6qjwn3t">
        {list.map(e => <EventCard key={e.id} w={w} e={e} data-id="element-2402" data-mp="1uasx3gkkorfa" />)}
      </ul>
    </div>;
}
function EventCard({
  w,
  e
}: {
  w: WorldData;
  e: GameEvent;
}) {
  const [showRewards, setShowRewards] = useState(false);
  const [showPositions, setShowPositions] = useState(false);
  const ep = useEventPositions();
  const eventPos = ep ? ep.positions.filter(p => p.eventId === e.id) : [];
  const schedules = (w.idx.schedulesByEvent.get(e.id) ?? []).slice().sort((a, b) => (a.weekday ?? -1) - (b.weekday ?? -1) || (a.hour ?? 0) - (b.hour ?? 0) || (a.minute ?? 0) - (b.minute ?? 0));
  const status = eventStatusSummary(w, e);
  const rooms = status.rooms;
  const eventBags = (w.idx.eventBagsByEvent.get(e.id) ?? []).filter(b => b.active !== false);
  const policy = invasionPolicies.find(p => p.eventIds.includes(e.id));
  const isInvasion = (e.type ?? '').toLowerCase().includes('invasion') || e.invasionIndex !== undefined;
  const mapIds = Array.from(new Set([...e.possibleMapIds, ...w.eventMapChoices.filter(c => c.eventId === e.id && c.mapId !== undefined).map(c => c.mapId!)]));
  const bagIds = Array.from(new Set(eventBags.map(b => b.bagId)));
  return <li id={eventAnchor(e.id)} className="scroll-mt-24 flex flex-col rounded-2xl border border-white/10 bg-[#0A1628] p-3.5 md:p-5" data-id="element-2403" data-mp="3s56rsidy477c">
      <div className="flex flex-wrap items-start justify-between gap-2" data-id="element-2404" data-mp="3w4xof2q5lhjs">
        <h2 className="flex items-center gap-1 font-cinzel text-lg md:text-xl font-bold text-white" data-id="element-2405" data-mp="4rb5rnt0w06e">
          <Link to={eventPath(e.id)} className="hover:text-[#D4AF37]" data-id="element-2406" data-mp="9qxky2sevqhu">{e.name}</Link>
          <CopyLinkButton path={eventPath(e.id)} label={`Copiar enlace de ${e.name}`} iconOnly data-id="element-2407" data-mp="eqahjf934bxm" />
        </h2>
        <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${status.event === 'habilitado' ? 'bg-emerald-400/10 text-emerald-200' : 'bg-white/[0.05] text-gray-400'}`} data-id="element-2408" data-mp="jo28a5xzts08">
          {eventConfigLabel[status.event]}
        </span>
      </div>
      {e.type && <p className="mt-0.5 text-xs text-gray-500" data-id="element-2409" data-mp="3l56e2mswm1pq">{isInvasion ? 'Invasión' : e.type}</p>}
      {status.servers.length > 0 && <ul aria-label="Subservidores" className="mt-2 flex flex-wrap gap-1.5" data-id="element-2410" data-mp="3v4niz20c0bkn">
          {status.servers.map(s => <li key={s.server} className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${s.enabled ? 'border-emerald-400/25 text-emerald-200' : 'border-white/10 text-gray-500 line-through decoration-gray-500/60'}`} data-id="element-2411" data-mp="cpwcrgwu90j0">
              {s.server}
              <span className="sr-only" data-id="element-2412" data-mp="hqk27y5f59b4">{s.enabled ? ' habilitado' : ' deshabilitado'}</span>
            </li>)}
        </ul>}

      {mapIds.length > 0 && <p className="mt-3 flex flex-wrap items-center gap-1.5 text-sm text-gray-300" data-id="element-2413" data-mp="2oiunqvcr036k">
          <span className="text-gray-400" data-id="element-2414" data-mp="2jjhzc668ao1v">{e.mapSelection === 'random' ? 'Uno de estos mapas:' : 'Mapas:'}</span>
          {mapIds.map(id => <Link key={id} to={mapPath(id)} className="rounded-full border border-white/15 px-2.5 py-0.5 text-xs font-semibold text-gray-200 hover:border-[#D4AF37]/50" data-id="element-2415" data-mp="13z7np78vy3j9">{mapName(w, id)}</Link>)}
        </p>}

      <div className="mt-3" data-id="element-2416" data-mp="u7uvufn37dd8">
        <p className="flex items-center gap-1.5 text-sm font-semibold text-white" data-id="element-2417" data-mp="u71yu6dxk28a"><Clock className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" data-id="element-2418" data-mp="g8bzek3n1qgr" /> Horario configurado</p>
        {schedules.length ? <ul className="mt-1.5 flex flex-wrap gap-1.5" data-id="element-2419" data-mp="kc1iiu1jnfue">
            {schedules.slice(0, 24).map(s => <li key={s.id} className="rounded-lg bg-white/[0.05] px-2 py-1 font-mono text-xs text-gray-200" data-id="element-2420" data-mp="6d1tvme170y8">
                {s.weekday !== undefined && WEEKDAYS[s.weekday] ? `${WEEKDAYS[s.weekday].slice(0, 3)} ` : ''}
                {pad(s.hour)}:{pad(s.minute)}
              </li>)}
            {schedules.length > 24 && <li className="px-1 py-1 text-xs text-gray-500" data-id="element-2421" data-mp="sgyogpqsrbq3">+{schedules.length - 24}</li>}
          </ul> : <p className="mt-1 text-sm text-gray-400" data-id="element-2422" data-mp="ofd6ipzeo791">Sin horario fijo en la exportación.</p>}
        {isInvasion && <p className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-400" data-id="element-2423" data-mp="jnbw79lssqln">
            <Keyboard className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2424" data-mp="eoqxjem72ezb" /> Confírmalo en el juego con la tecla H y en los avisos de Discord.
          </p>}
      </div>

      {eventPos.length > 0 && <div className="mt-3" data-id="element-2425" data-mp="4qkswp906uqr">
          <button type="button" onClick={() => setShowPositions(v => !v)} aria-expanded={showPositions} className="flex h-9 items-center gap-1.5 text-sm font-semibold text-white hover:text-[#F3E5AB]" data-id="element-2426" data-mp="4tqkqc90oaq6">
            <MapPin className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" data-id="element-2427" data-mp="3ryev0p4qxrbr" /> Posiciones de monstruos ({eventPos.length})
          </button>
          {showPositions && <EventPositionList w={w} positions={eventPos} max={40} showMonster data-id="element-2428" data-mp="1q60d3q27cmvc" />}
        </div>}

      {rooms.length > 0 && <div className="mt-3" data-id="element-2429" data-mp="1xo80ijs5menw">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-white" data-id="element-2430" data-mp="22nkudohg0lzz"><DoorOpen className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" data-id="element-2431" data-mp="22lhy4mpp5wmt" /> Salas y nivel de entrada</p>
          <p className="mt-0.5 text-xs text-gray-500" data-id="element-2432" data-mp="27gdz4ccwvuc2">{roomStatesText(rooms)}. Cada sala tiene su propio estado; una sala deshabilitada no desactiva el evento.</p>
          <ul className="mt-1.5 grid gap-1 text-sm text-gray-300 sm:grid-cols-2" data-id="element-2433" data-mp="1eqxcjvbz37nr">
            {rooms.map(({
          room: r,
          state
        }) => {
          const off = state === 'deshabilitada' || state === 'entrada-cerrada';
          const roomServers = r.serverFlags.length && serversText(r.serverFlags) !== serversText(status.servers) ? serversText(r.serverFlags) : '';
          return <li key={r.id} className={`rounded-lg px-2.5 py-1.5 ${off ? 'bg-white/[0.015] text-gray-500' : 'bg-white/[0.03]'}`} data-id="element-2434" data-mp="301x6ujyb2bma">
                  <span className="flex justify-between gap-2" data-id="element-2435" data-mp="346n0s39elg4k">
                    <span data-id="element-2436" data-mp="7a9qhowp00bh">Sala {r.roomNumber ?? '—'}</span>
                    <span className="tabular-nums text-gray-400" data-id="element-2437" data-mp="cae519e1ikz2">
                      {state === 'rango-invalido' ? `Nv. ${r.levelMin ?? '?'}–${r.levelMax ?? '?'}` : r.levelMin !== undefined ? `Nv. ${r.levelMin}–${r.levelMax ?? '…'}` : 'Nivel no documentado'}
                      {r.masterRequired ? ' · Master' : ''}
                    </span>
                  </span>
                  {state !== 'activa' && <span className={`mt-0.5 block text-[11px] font-semibold ${state === 'rango-invalido' ? 'text-amber-200' : 'text-gray-500'}`} data-id="element-2438" data-mp="3ufhef6b1hj5a">
                      {state === 'rango-invalido' ? 'Rango de nivel inválido en la configuración: pendiente de corregir por el staff' : `${roomStateLabel[state]} en la configuración`}
                    </span>}
                  {roomServers && <span className="mt-0.5 block text-[11px] text-gray-500" data-id="element-2439" data-mp="2z7f5l6mpcugv">Subservidores: {roomServers}</span>}
                </li>;
        })}
          </ul>
        </div>}

      {eventAudit[e.id] && <div className="mt-3" data-id="element-2440" data-mp="2pg2fokkgsedh">
          <ConfigAuditPanel fact={eventAudit[e.id]} title={e.id.startsWith('invasion:') ? 'Estado documentado' : 'Acceso y activación'} data-id="element-2441" data-mp="2pf9cftkps703" />
        </div>}

      {(bagIds.length > 0 || policy) && <div className="mt-3" data-id="element-2442" data-mp="2fgtlprplmzr4">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-white" data-id="element-2443" data-mp="2bhkt4q6ap7x5"><Gift className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" data-id="element-2444" data-mp="2bjp7y35qfrc7" /> Recompensas</p>
          {policy ? <p className="mt-1 text-sm text-gray-300" data-id="element-2445" data-mp="21j52spf3e2op">
              Consulta las recompensas aprobadas en la{' '}
              <Link to={entryPathById(policy.guideEntryId)} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4" data-id="element-2446" data-mp="4renzae1x5iv">ficha de {policy.name}</Link>.
              <span className="mt-0.5 block text-xs text-gray-500" data-id="element-2447" data-mp="1u80glclxj429">La lista de ítems exportada para esta invasión está en revisión por el staff.</span>
            </p> : <>
              <p className="mt-1 text-sm text-gray-400" data-id="element-2448" data-mp="1k9ufuybv03yz">{bagIds.map(id => w.bags.get(id) ? bagName(w, w.bags.get(id)!) : `Bolsa ${id}`).join(' · ')} — cada premio es uno de los resultados posibles.</p>
              <button type="button" onClick={() => setShowRewards(v => !v)} aria-expanded={showRewards} className="mt-1 h-9 text-sm font-semibold text-[#D4AF37] hover:text-[#F3E5AB]" data-id="element-2449" data-mp="2cyi492rwn9gr">
                {showRewards ? 'Ocultar resultados posibles' : 'Ver resultados posibles'}
              </button>
              {showRewards && <ul className="mt-1 flex flex-wrap gap-1.5" data-id="element-2450" data-mp="27ydndbn34cq0">
                  {bagIds.flatMap(id => bagContents(w, id)).filter(c => !c.withheld).slice(0, 60).map(c => <li key={`${c.itemKey}-${c.levelMin ?? ''}`} data-id="element-2451" data-mp="wkrnowjlmsq8">
                      <Link to={itemPath(c.itemKey)} className="inline-flex rounded-full border border-white/15 px-2.5 py-1 text-xs text-gray-200 hover:border-[#D4AF37]/50" data-id="element-2452" data-mp="wi5k17ztwxda">
                        {c.name}{levelRange(c.levelMin, c.levelMax) ? ` ${levelRange(c.levelMin, c.levelMax)}` : ''}
                      </Link>
                    </li>)}
                </ul>}
            </>}
        </div>}

      <p className="mt-auto pt-3 flex items-center gap-1.5 text-[11px] text-gray-500" data-id="element-2453" data-mp="2s0qdkulogkqv">
        <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2454" data-mp="2n2yjbtxk8mji" /> Documentado en la configuración del {EXPORT_DATE_LABEL} · no confirma la versión desplegada hoy · no comprobado en el juego
      </p>
    </li>;
}